// AdminUsers — Owner-only User-Liste mit Stat-Cards + Toolbar + Tabelle
// + Detail-Drawer. Tier-Verteilung jetzt hier statt im „Tier-Verwaltung"-Tab.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import {
  createAdminUser,
  deleteAdminUser,
  listAdminUsers,
  patchAdminUser,
  type AdminUserRow,
  type CreateUserResponse,
  type UserRole,
} from '@/lib/admin-users-api';
import { activateCoaching, cancelCoaching } from '@/lib/coaching-api';
import { UserStatsCards } from './users/user-stats-cards';
import {
  UserToolbar,
  type OnlineFilter,
  type StripeFilter,
  type TierFilter,
} from './users/user-toolbar';
import { UserRow } from './users/user-row';
import { UserDetailDrawer } from './users/user-detail-drawer';
import { AddMemberDialog } from './users/add-member-dialog';
import { DeleteConfirmDialog } from './users/delete-confirm-dialog';
import { CoachingActivationDialog } from './users/coaching-activation-dialog';

export function AdminUsers() {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Filter
  const [query, setQuery] = useState('');
  const [tierF, setTierF] = useState<TierFilter>('all');
  const [onlineF, setOnlineF] = useState<OnlineFilter>('all');
  const [stripeF, setStripeF] = useState<StripeFilter>('all');

  // Drawer + Dialogs
  const [selected, setSelected] = useState<AdminUserRow | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminUserRow | null>(null);
  const [showCoachingDialog, setShowCoachingDialog] = useState<AdminUserRow | null>(null);

  // Live „now" für Online-Logik — jede Minute aktualisieren.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await listAdminUsers(accessToken);
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.email.toLowerCase().includes(q) && !(r.nickname?.toLowerCase().includes(q) ?? false)) return false;
      if (tierF !== 'all' && r.tier !== tierF) return false;
      if (onlineF !== 'all') {
        const isOn = r.last_seen ? now.getTime() - Date.parse(r.last_seen) < 5 * 60_000 : false;
        if (onlineF === 'online' && !isOn) return false;
        if (onlineF === 'offline' && isOn) return false;
      }
      if (stripeF !== 'all') {
        const active = r.stripe_status === 'active';
        if (stripeF === 'active' && !active) return false;
        if (stripeF === 'inactive' && active) return false;
      }
      return true;
    });
  }, [rows, query, tierF, onlineF, stripeF, now]);

  async function handleSaveTier(userId: string, role: UserRole) {
    if (!accessToken) return;
    await patchAdminUser(accessToken, userId, { role });
    setSelected(null);
    await reload();
  }

  function handleDeleteClick(userId: string) {
    const u = rows.find((r) => r.id === userId);
    if (u) {
      setSelected(null);
      setPendingDelete(u);
    }
  }

  async function handleDeleteConfirm(reason: string) {
    if (!accessToken || !pendingDelete) return;
    await deleteAdminUser(accessToken, pendingDelete.id, { reason });
    setPendingDelete(null);
    await reload();
  }

  async function handleCreate(input: { email: string; role: UserRole }): Promise<CreateUserResponse> {
    if (!accessToken) throw new Error('Nicht eingeloggt');
    const res = await createAdminUser(accessToken, input);
    await reload();
    return res;
  }

  return (
    <div className="space-y-4">
      <UserStatsCards users={rows} />
      <UserToolbar
        query={query}
        onQueryChange={setQuery}
        tier={tierF}
        onTierChange={setTierF}
        online={onlineF}
        onOnlineChange={setOnlineF}
        stripe={stripeF}
        onStripeChange={setStripeF}
        onAddClick={() => setShowAdd(true)}
      />

      <div className="glass-strong overflow-hidden rounded-2xl border border-white/5">
        {error ? (
          <div className="m-3 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            {error}
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-[12px] text-neutral-500">Lade User…</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="bg-neutral-900/60 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-3 py-2 text-left"> </th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Tier</th>
                <th className="px-3 py-2 text-left">DS</th>
                <th className="px-3 py-2 text-left">Seit</th>
                <th className="px-3 py-2 text-left">Online</th>
                <th className="px-3 py-2 text-left">Stripe</th>
                <th className="px-3 py-2 text-right">Credits</th>
                <th className="px-3 py-2 text-right"> </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <UserRow key={u.id} user={u} now={now} onSelect={setSelected} />
              ))}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-neutral-500">
                    Keine User passend zum Filter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>

      <UserDetailDrawer
        user={selected}
        onClose={() => setSelected(null)}
        onSaveTier={handleSaveTier}
        onDelete={handleDeleteClick}
        onActivateCoaching={(userId) => {
          const u = rows.find((r) => r.id === userId);
          if (u) {
            setSelected(null);
            setShowCoachingDialog(u);
          }
        }}
        onCancelCoaching={async (userId) => {
          if (!accessToken) return;
          await cancelCoaching(accessToken, userId);
          await reload();
        }}
        onCreditsChanged={(userId, newBalance) => {
          setRows((prev) =>
            prev.map((r) => (r.id === userId ? { ...r, balance: newBalance } : r)),
          );
          setSelected((prev) => (prev && prev.id === userId ? { ...prev, balance: newBalance } : prev));
        }}
      />
      {showAdd ? (
        <AddMemberDialog onClose={() => setShowAdd(false)} onCreate={handleCreate} />
      ) : null}
      {pendingDelete ? (
        <DeleteConfirmDialog
          user={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={handleDeleteConfirm}
        />
      ) : null}
      {showCoachingDialog ? (
        <CoachingActivationDialog
          user={showCoachingDialog}
          onClose={() => setShowCoachingDialog(null)}
          onConfirm={async (input) => {
            if (!accessToken) return;
            await activateCoaching(accessToken, showCoachingDialog.id, input);
            setShowCoachingDialog(null);
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}
